import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Search, Star, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { AccountingSubNav } from '@/features/accounting/components/AccountingSubNav';
import { getApiErrorMessage } from '@/shared/api/axios';
import { SupplierScorecardPanel } from '../components/SupplierScorecardPanel';
import { getSupplierScorecard, listScorecards } from '../api/procurementRfqApi';
import { SUPPLIER_RATING_DIMENSIONS, SUPPLIER_RATING_MAX } from '../constants';
import type { SupplierRating, SupplierScorecard } from '../types';

/**
 * Every supplier that has ever been rated, as a wall of scorecards.
 *
 * The cards are the server's aggregate (GET /supplier-scorecard/scorecards),
 * which recomputes from the stored ratings on each request — nothing is
 * averaged in the browser. Opening a card fetches that supplier's individual
 * ratings (GET /supplier-scorecard/supplier/:supplierId) so a score can always
 * be traced back to the deliveries that produced it.
 *
 * Reached through the `accounts` workspace gate on its route, in line with the
 * other Accounting screens; the rating permissions are enforced server-side.
 */
export function SupplierScorecardsPage() {
  const [cards, setCards] = useState<SupplierScorecard[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [openSupplierId, setOpenSupplierId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ scorecard: SupplierScorecard; ratings: SupplierRating[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCards(await listScorecards());
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load supplier scorecards.'));
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openSupplierId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    getSupplierScorecard(openSupplierId)
      .then(result => { if (!cancelled) setDetail(result); })
      .catch(err => {
        if (cancelled) return;
        setDetail(null);
        toast.error(getApiErrorMessage(err, 'Could not load that supplier’s ratings.'));
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [openSupplierId]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? cards.filter(card => (card.supplierName || card.supplierId).toLowerCase().includes(q))
    : cards;

  const rated = cards.filter(card => card.ratingCount > 0);
  const averageOverall = rated.length
    ? rated.reduce((sum, card) => sum + (card.overallScore ?? 0), 0) / rated.length
    : null;

  return (
    <div className="">
      <AccountingSubNav />
      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <PageHeader
          icon={Star}
          eyebrow="Finance"
          title="Supplier Scorecards"
          description={<>Delivery, quality, price and responsiveness averaged across every recorded rating — the same figures that rank bidders on an RFQ</>}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Suppliers rated', value: String(rated.length) },
            { label: 'Ratings recorded', value: String(cards.reduce((sum, card) => sum + card.ratingCount, 0)) },
            { label: 'Average overall score', value: averageOverall === null ? '—' : `${averageOverall.toFixed(1)}/${SUPPLIER_RATING_MAX}` },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05, ease: [0.4, 0, 0.2, 1] }}
              className="rounded-xl p-4 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
            >
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">{stat.label}</p>
              <p className="text-2xl font-black text-slate-900 dark:text-slate-100 mt-1">{stat.value}</p>
            </motion.div>
          ))}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72"
            placeholder="Search supplier…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
            <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto mb-3">
              <Star className="w-6 h-6" />
            </div>
            <p className="text-sm text-slate-400">
              {cards.length === 0 ? 'No supplier has been rated yet.' : 'No supplier matches that search.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(card => (
              <button
                key={card.supplierId}
                type="button"
                onClick={() => setOpenSupplierId(card.supplierId)}
                className="text-left transition-transform hover:-translate-y-0.5"
              >
                <SupplierScorecardPanel scorecard={card} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Drill-down: the individual ratings behind one card. */}
      {openSupplierId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-indigo-600">
              <div className="flex items-center gap-2 text-white">
                <Star className="w-5 h-5" />
                <h2 className="text-lg font-bold">
                  {detail?.scorecard.supplierName || cards.find(c => c.supplierId === openSupplierId)?.supplierName || 'Supplier'}
                </h2>
              </div>
              <button type="button" onClick={() => setOpenSupplierId(null)}><X className="w-5 h-5 text-white/80" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {detailLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
              ) : !detail ? (
                <p className="text-sm text-slate-400 text-center py-12">Nothing to show for this supplier.</p>
              ) : (
                <>
                  <SupplierScorecardPanel scorecard={detail.scorecard} />

                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      Ratings behind this score ({detail.ratings.length})
                    </h3>
                    {detail.ratings.length === 0 ? (
                      <p className="text-sm text-slate-400">No individual ratings recorded yet.</p>
                    ) : (
                      <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 dark:bg-slate-800">
                            <tr>
                              {['Rated', 'Source', ...SUPPLIER_RATING_DIMENSIONS.map(d => d.label), 'Overall'].map(h => (
                                <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-500">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {detail.ratings.map(rating => (
                              <tr key={rating._id} className="bg-white dark:bg-slate-900">
                                <td className="px-3 py-2 font-mono text-xs text-slate-500">
                                  {rating.ratedAt ? new Date(rating.ratedAt).toLocaleDateString('en-IN') : '—'}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-500 uppercase">{rating.sourceType}</td>
                                {SUPPLIER_RATING_DIMENSIONS.map(dimension => {
                                  const value = rating.scores?.[dimension.key];
                                  return (
                                    <td key={dimension.key} className="px-3 py-2 text-slate-600 dark:text-slate-300">
                                      {value === null || value === undefined ? '—' : value}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-200">
                                  {rating.overallScore === null || rating.overallScore === undefined
                                    ? '—'
                                    : rating.overallScore.toFixed(1)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
              <button
                type="button"
                onClick={() => setOpenSupplierId(null)}
                className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierScorecardsPage;
